import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  cn,
  Combobox,
  DateInput,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  MultiCombobox,
  SimpleSelect,
} from '@kalos/ui';
import { Filter } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';

import { EVENT_STATUS_LIST, OPTION_ALL, PROJECT_STATUS_LIST } from '../../../../constants';
import { useJobSubTypesQuery } from '../../../../hooks/react-query/useJobSubTypesQuery';
import { useJobTypesQuery } from '../../../../hooks/react-query/useJobTypesQuery';
import { useBatchUserQuery } from '../../../../hooks/react-query/useUserQuery';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { useMediaQuery } from '../../../../hooks/useMedia';
import { TimesheetDepartmentPickerV2 } from '../../Pickers/newPickers/QueryPickerV2';
import { renderDepartment, techniciansUserFilter } from '../../Pickers/newPickers/utils';
import { JOB_SEARCH_SECTIONS, type JobSearchSectionId } from './jobSearchSections';
import { useUnifiedJobSearchFormContext } from './utils';

// Combined unique statuses for unified search (service call + project)
const ALL_STATUSES = [...new Set([...EVENT_STATUS_LIST, ...PROJECT_STATUS_LIST])];

const makeStatusOptions = (statuses: string[]) =>
  statuses.map((label) => ({
    label,
    value: label,
  }));

const SECTOR_OPTIONS = [
  { label: OPTION_ALL, value: '0' },
  { label: 'Residential', value: '1' },
  { label: 'Commercial', value: '2' },
];

const defaultAdvancedSettingsAccordionValue = 'advanced';

// Common input styling to match prototype (white background, subtle border)
const inputClassName = 'bg-white border-gray-300 rounded-md';
const selectTriggerClassName = 'bg-white border-gray-300 rounded-md h-auto text-xs';

/**
 * The job search filters. `section` renders one panel's controls for the phone filter sheet:
 * one column, no form of its own (the sheet is the form), no collapse, accordion or Reset.
 */
export const UnifiedJobSearchForm = ({ section }: { section?: JobSearchSectionId } = {}) => {
  const [show, setShow] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width: 640px)');
  const form = useUnifiedJobSearchFormContext();
  const [advancedSettingsAccordionValue, setAdvancedSettingsAccordionValue] = useLocalStorage(
    'unifiedJobSearchAdvancedSettingsAccordionValue',
    defaultAdvancedSettingsAccordionValue,
  );

  const techniciansQuery = useBatchUserQuery({
    filters: techniciansUserFilter,
    select(data) {
      return data.results.map((technician) => ({
        label: `${technician.firstname} ${technician.lastname}`,
        value: technician.id.toString(),
      }));
    },
  });

  const jobTypesQuery = useJobTypesQuery({
    select(data) {
      return [
        { label: OPTION_ALL, value: '0' },
        ...(data?.map((jt) => ({
          label: jt.name,
          value: jt.id.toString(),
        })) ?? []),
      ];
    },
  });

  const jobSubTypesQuery = useJobSubTypesQuery({});

  const jobSubTypeOptions = useMemo(() => {
    return [
      { label: OPTION_ALL, value: '0' },
      ...(jobSubTypesQuery.data?.results.map((jst) => ({
        label: jst.name,
        value: jst.id.toString(),
      })) ?? []),
    ];
  }, [jobSubTypesQuery.data?.results]);

  // Watch project/service call checkboxes to dynamically filter status options
  const projectsOnly = form.watch('projectsOnly');
  const serviceCallsOnly = form.watch('serviceCallsOnly');

  const jobStatusOptions = useMemo(() => {
    if (projectsOnly) {
      return makeStatusOptions(PROJECT_STATUS_LIST);
    }
    if (serviceCallsOnly) {
      return makeStatusOptions(EVENT_STATUS_LIST);
    }
    return makeStatusOptions(ALL_STATUSES);
  }, [projectsOnly, serviceCallsOnly]);

  // Each control once: the desktop rows below and the phone filter sheet's panels both use these.
  const fields = {
    technicianAssigned: (
      <FormField
        control={form.control}
        name="technicianAssigned"
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>Technician Assigned</FormLabel>
              <Combobox
                onChange={(value) => {
                  field.onChange(value || '');
                }}
                value={field.value ?? ''}
                emptyLabel="Unassigned"
                disabled={techniciansQuery.isPending}
                values={techniciansQuery.data ?? []}
                placeholder="Search Technician"
              />
            </FormItem>
          );
        }}
      />
    ),
    isResidential: (
      <FormField
        control={form.control}
        name="isResidential"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sector</FormLabel>
            <SimpleSelect
              placeholder={OPTION_ALL}
              selectedValue={(field.value ?? 0).toString()}
              onChange={(value) => field.onChange(parseInt(value, 10) || 0)}
              values={SECTOR_OPTIONS}
            />
          </FormItem>
        )}
      />
    ),
    departmentId: (
      <FormField
        control={form.control}
        name="departmentId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Department</FormLabel>
            <FormControl>
              <TimesheetDepartmentPickerV2
                renderItem={renderDepartment}
                queryArgs={{
                  filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
                }}
                onSelect={(val) => {
                  field.onChange(val?.id || 0);
                }}
                selected={(field?.value || '0').toString()}
                placeholder="-- All --"
                triggerClassName={selectTriggerClassName}
              />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    jobTypeId: (
      <FormField
        control={form.control}
        name="jobTypeId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Type</FormLabel>
            <SimpleSelect
              placeholder={OPTION_ALL}
              selectedValue={(field.value ?? 0).toString()}
              onChange={(value) => {
                field.onChange(parseInt(value, 10) || 0);
                // Reset subtype when type changes
                form.setValue('jobSubtypeId', 0);
              }}
              values={jobTypesQuery.data ?? [{ label: OPTION_ALL, value: '0' }]}
              disabled={jobTypesQuery.isPending}
            />
          </FormItem>
        )}
      />
    ),
    jobSubtypeId: (
      <FormField
        control={form.control}
        name="jobSubtypeId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Subtype</FormLabel>
            <SimpleSelect
              placeholder={OPTION_ALL}
              selectedValue={(field.value ?? 0).toString()}
              onChange={(value) => field.onChange(parseInt(value, 10) || 0)}
              values={jobSubTypeOptions}
              disabled={jobSubTypesQuery.isPending}
            />
          </FormItem>
        )}
      />
    ),
    jobStatus: (
      <FormField
        control={form.control}
        name="jobStatus"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Status</FormLabel>
            <MultiCombobox
              placeholder="Search status..."
              emptyLabel="All Statuses"
              value={field.value ? field.value.split(',').filter(Boolean) : []}
              onChange={(arr) => field.onChange(arr.join(','))}
              values={jobStatusOptions}
            />
          </FormItem>
        )}
      />
    ),
    dateStartedFrom: (
      <FormField
        control={form.control}
        name="dateStartedFrom"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date Started - From</FormLabel>
            <DateInput value={field.value} onChange={field.onChange} />
          </FormItem>
        )}
      />
    ),
    dateStartedTo: (
      <FormField
        control={form.control}
        name="dateStartedTo"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date Started - To</FormLabel>
            <DateInput value={field.value} onChange={field.onChange} />
          </FormItem>
        )}
      />
    ),
    servicesNeeded: (
      <FormField
        control={form.control}
        name="servicesNeeded"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Services Needed</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Service description" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    logNotes: (
      <FormField
        control={form.control}
        name="logNotes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Notes</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    firstName: (
      <FormField
        control={form.control}
        name="firstName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>First Name</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Customer first name" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    lastName: (
      <FormField
        control={form.control}
        name="lastName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Last Name</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Customer last name" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    businessName: (
      <FormField
        control={form.control}
        name="businessName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Business Name</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Business or company name" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    propertyAddress: (
      <FormField
        control={form.control}
        name="propertyAddress"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Street address" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    city: (
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem>
            <FormLabel>City</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="City name" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    state: (
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem>
            <FormLabel>State</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="FL" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    zip: (
      <FormField
        control={form.control}
        name="zip"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Zip Code</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Zip code" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    jobNumber: (
      <FormField
        control={form.control}
        name="jobNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Job Number</FormLabel>
            <FormControl>
              <Input
                {...field}
                className={inputClassName}
                placeholder="Job #"
                onChange={(e) => {
                  // Only allow numbers and dashes
                  const value = e.target.value.replace(/[^0-9-]/g, '');
                  field.onChange(value);
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    poNumber: (
      <FormField
        control={form.control}
        name="poNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tracking Number</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Job tracking number" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    name: (
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Brief Description</FormLabel>
            <FormControl>
              <Input {...field} className={inputClassName} placeholder="Brief description" />
            </FormControl>
          </FormItem>
        )}
      />
    ),
    dateEndedFrom: (
      <FormField
        control={form.control}
        name="dateEndedFrom"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date Ended - From</FormLabel>
            <DateInput value={field.value} onChange={field.onChange} />
          </FormItem>
        )}
      />
    ),
    dateEndedTo: (
      <FormField
        control={form.control}
        name="dateEndedTo"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Date Ended - To</FormLabel>
            <DateInput value={field.value} onChange={field.onChange} />
          </FormItem>
        )}
      />
    ),
    projectsOnly: (
      <FormField
        control={form.control}
        name="projectsOnly"
        render={({ field }) => (
          <FormItem className="flex items-center gap-2 space-y-0">
            <FormControl>
              <Checkbox
                disabled={field.disabled}
                onBlur={field.onBlur}
                ref={field.ref}
                name={field.name}
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(!!state);
                  if (state) {
                    form.setValue('serviceCallsOnly', false);
                    // Reset status if any selected value isn't a valid project status
                    const currentStatus = form.getValues('jobStatus');
                    if (currentStatus) {
                      const validStatuses = currentStatus
                        .split(',')
                        .filter((s) => PROJECT_STATUS_LIST.includes(s));
                      form.setValue('jobStatus', validStatuses.join(','));
                    }
                  }
                }}
              />
            </FormControl>
            <FormLabel className="cursor-pointer">Projects Only</FormLabel>
          </FormItem>
        )}
      />
    ),
    serviceCallsOnly: (
      <FormField
        control={form.control}
        name="serviceCallsOnly"
        render={({ field }) => (
          <FormItem className="flex items-center gap-2 space-y-0">
            <FormControl>
              <Checkbox
                disabled={field.disabled}
                onBlur={field.onBlur}
                ref={field.ref}
                name={field.name}
                checked={field.value}
                onCheckedChange={(state) => {
                  field.onChange(!!state);
                  if (state) {
                    form.setValue('projectsOnly', false);
                    // Reset status if any selected value isn't a valid service call status
                    const currentStatus = form.getValues('jobStatus');
                    if (currentStatus) {
                      const validStatuses = currentStatus
                        .split(',')
                        .filter((s) => EVENT_STATUS_LIST.includes(s));
                      form.setValue('jobStatus', validStatuses.join(','));
                    }
                  }
                }}
              />
            </FormControl>
            <FormLabel className="cursor-pointer">Service Calls Only</FormLabel>
          </FormItem>
        )}
      />
    ),
  } satisfies Record<string, ReactNode>;

  if (section) {
    const sectionFields =
      JOB_SEARCH_SECTIONS.find((candidate) => candidate.id === section)?.fields ?? [];
    return (
      <div className="flex flex-col gap-4">
        {sectionFields.map((name) => (
          <Fragment key={name}>{fields[name]}</Fragment>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Mobile Filters Toggle */}
      {isMobile && (
        <div className="mb-3">
          <Button
            className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3"
            variant="ghost"
            onClick={() => {
              setShow((isShown) => !isShown);
            }}
          >
            <span className="flex items-center gap-2">
              <Filter size={16} /> Filters
            </span>
            <span className={cn('transition-transform', show && 'rotate-180')}>▼</span>
          </Button>
        </div>
      )}
      <div
        tabIndex={show ? undefined : -1}
        className={cn('grid transition-all', show ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
      >
        <form
          aria-hidden={!show}
          onSubmit={form.handleSubmit(console.log)}
          className="overflow-hidden p-0.5 transition-[0.2s]"
        >
          {/* Row 1: Main Filters (6 columns on web) */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-3 [&>*]:min-w-0">
            {fields.technicianAssigned}

            {fields.isResidential}

            {fields.departmentId}

            {fields.jobTypeId}

            {fields.jobSubtypeId}

            {fields.jobStatus}
          </div>

          {/* Row 2: Date filters, Services, Notes, Reset */}
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-3 [&>*]:min-w-0">
            {fields.dateStartedFrom}

            {fields.dateStartedTo}

            {fields.servicesNeeded}

            {fields.logNotes}

            <div className="flex items-end">
              <Button
                className="w-full border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => form.reset()}
                type="reset"
                variant="outline"
              >
                Reset Filters
              </Button>
            </div>
          </div>

          {/* Advanced Options */}
          <Accordion
            value={advancedSettingsAccordionValue}
            className="mt-4"
            onValueChange={setAdvancedSettingsAccordionValue}
            type="single"
            collapsible
          >
            <AccordionItem value="advancedSearchOptions" className="border-none">
              <AccordionTrigger className="text-primary max-w-max py-2 hover:no-underline">
                {advancedSettingsAccordionValue === 'advancedSearchOptions'
                  ? 'Hide Advanced Options'
                  : 'Show Advanced Options'}
              </AccordionTrigger>

              <AccordionContent className="space-y-4 pt-2">
                {/* Customer Name Search */}
                <div>
                  <h4 className="mb-2 text-sm font-medium">Customer Name Search</h4>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
                    {fields.firstName}

                    {fields.lastName}

                    {fields.businessName}
                  </div>
                </div>

                {/* Property Level Search */}
                <div>
                  <h4 className="mb-2 text-sm font-medium">Property Level Search</h4>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
                    {fields.propertyAddress}

                    {fields.city}

                    {fields.state}

                    {fields.zip}
                  </div>
                </div>

                {/* Additional Filters */}
                <div>
                  <h4 className="mb-2 text-sm font-medium">Additional Filters</h4>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
                    {fields.jobNumber}

                    {fields.poNumber}

                    {fields.name}
                  </div>
                </div>

                {/* Date Ended Range */}
                <div>
                  <h4 className="mb-2 text-sm font-medium">Date Ended Range</h4>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    {fields.dateEndedFrom}

                    {fields.dateEndedTo}
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="flex flex-wrap gap-4">
                  {fields.projectsOnly}

                  {fields.serviceCallsOnly}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </form>
      </div>
    </>
  );
};
